/**
 * Patient Journey Timeline Component
 * Shows the complete patient experience from initial consultation to recovery
 * Used on the main hub site (drdnreddyortho.com)
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useSite } from '../contexts/SiteContext';
import { 
  Phone, 
  ClipboardList, 
  Stethoscope, 
  MessageSquare,
  Calendar,
  Activity,
  HeartPulse,
  ThumbsUp,
  ArrowRight,
  Clock,
  MapPin,
  CheckCircle2
} from 'lucide-react';

export const PatientJourneyTimeline = () => {
  const { theme, doctor } = useSite();

  const journeySteps = [
    {
      step: 1,
      title: 'Initial Contact',
      subtitle: 'Schedule Your Visit',
      icon: Phone,
      duration: 'Day 0',
      description: 'Book your appointment through our website, phone, or WhatsApp. Our team will confirm your slot and send you any pre-visit forms.',
      highlights: [
        'Easy online booking available',
        'Same-day appointments when available',
        'Flexible scheduling options'
      ],
      action: 'Book Appointment',
      actionLink: doctor?.consultationLink
    },
    {
      step: 2,
      title: 'First Consultation',
      subtitle: 'Meet Dr. Reddy',
      icon: Stethoscope,
      duration: 'Day 1',
      description: 'Comprehensive evaluation including detailed history, physical examination, and review of any previous imaging. You\'ll have time to discuss all your concerns.',
      highlights: [
        'Thorough 30-45 minute consultation',
        'Detailed explanation of your condition',
        'Discussion of all treatment options'
      ],
      location: 'Yashoda Hospitals, HITEC City'
    },
    {
      step: 3,
      title: 'Diagnostic Workup',
      subtitle: 'Understanding Your Condition',
      icon: ClipboardList,
      duration: 'Week 1',
      description: 'If needed, additional investigations like X-rays, MRI, or specialized tests are ordered. Results are typically available within days.',
      highlights: [
        'State-of-the-art imaging facilities',
        'Quick turnaround on results',
        'Clear explanation of findings'
      ]
    },
    {
      step: 4,
      title: 'Treatment Plan',
      subtitle: 'Your Personalized Path',
      icon: MessageSquare,
      duration: 'Week 1-2',
      description: 'Based on diagnosis, we create a customized treatment plan together. You\'ll understand every option, from conservative care to surgical intervention.',
      highlights: [
        'Evidence-based treatment protocols',
        'Clear timeline and expectations',
        'Written plan provided to you'
      ]
    },
    {
      step: 5,
      title: 'Treatment Phase',
      subtitle: 'Getting You Better',
      icon: Activity,
      duration: 'Varies',
      description: 'Whether it\'s physiotherapy, injections, or surgery—you\'re supported every step. For surgical cases, our team ensures seamless coordination.',
      highlights: [
        'Minimally invasive techniques preferred',
        'Latest surgical technology',
        'Dedicated surgical coordinator'
      ]
    },
    {
      step: 6,
      title: 'Recovery & Rehab',
      subtitle: 'Your Comeback Journey',
      icon: HeartPulse,
      duration: 'Weeks to Months',
      description: 'Structured rehabilitation program with our physiotherapy partners. Regular follow-ups to track progress and adjust treatment as needed.',
      highlights: [
        'Personalized rehab protocol',
        'Regular progress assessments',
        'Direct WhatsApp access for questions'
      ]
    },
    {
      step: 7,
      title: 'Full Recovery',
      subtitle: 'Back to Your Life',
      icon: ThumbsUp,
      duration: 'Goal Achieved',
      description: 'Return to your normal activities, sports, and life. We remain available for any future concerns and annual check-ups if needed.',
      highlights: [
        '90%+ patient satisfaction',
        'Lifelong care relationship',
        'Ongoing support available'
      ]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-testid="patient-journey">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Your Patient Journey
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            From your first call to complete recovery&mdash;here&apos;s what to expect when you choose Dr. Deepthi Nandan Reddy for your care
          </p>
        </div>

        {/* Timeline - Desktop */}
        <div className="hidden lg:block max-w-6xl mx-auto relative">
          {/* Central line */}
          <div 
            className="absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2"
            style={{ backgroundColor: `${theme?.primaryHex || '#3b82f6'}20` }}
          />
          
          {journeySteps.map((step, index) => {
            const Icon = step.icon;
            const isLeft = index % 2 === 0;
            
            return (
              <div 
                key={step.step}
                className={`relative flex items-center mb-12 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}
              >
                {/* Card */}
                <div className={`w-5/12 ${isLeft ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                  <Card className="hover:shadow-lg transition-shadow border-0 shadow-md">
                    <CardHeader className="pb-2">
                      <div className={`flex items-center gap-3 ${isLeft ? 'justify-end' : 'justify-start'}`}>
                        <span 
                          className="text-sm font-semibold px-3 py-1 rounded-full"
                          style={{ 
                            backgroundColor: `${theme?.primaryHex || '#3b82f6'}15`,
                            color: theme?.primaryHex || '#3b82f6'
                          }}
                        >
                          {step.duration}
                        </span>
                      </div>
                      <CardTitle className="text-xl mt-2">{step.title}</CardTitle>
                      <p className="text-sm text-gray-500">{step.subtitle}</p>
                    </CardHeader>
                    <CardContent className={isLeft ? 'text-right' : 'text-left'}>
                      <p className="text-gray-600 mb-4">{step.description}</p>
                      <ul className={`space-y-2 ${isLeft ? 'text-right' : 'text-left'}`}>
                        {step.highlights.map((highlight, i) => (
                          <li 
                            key={i} 
                            className={`flex items-center gap-2 text-sm text-gray-700 ${isLeft ? 'justify-end' : 'justify-start'}`}
                          >
                            {!isLeft && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
                            {highlight}
                            {isLeft && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
                          </li>
                        ))}
                      </ul>
                      {step.location && (
                        <p className={`flex items-center gap-2 mt-4 text-sm text-gray-500 ${isLeft ? 'justify-end' : 'justify-start'}`}>
                          {!isLeft && <MapPin className="w-4 h-4" />}
                          {step.location}
                          {isLeft && <MapPin className="w-4 h-4" />}
                        </p>
                      )}
                      {step.actionLink && (
                        <a 
                          href={step.actionLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 mt-4 font-medium hover:underline ${isLeft ? 'flex-row-reverse' : ''}`}
                          style={{ color: theme?.primaryHex || '#3b82f6' }}
                        >
                          {isLeft && <ArrowRight className="w-4 h-4 rotate-180" />}
                          {step.action}
                          {!isLeft && <ArrowRight className="w-4 h-4" />}
                        </a>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Center icon */}
                <div className="absolute left-1/2 -translate-x-1/2 z-10">
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: theme?.primaryHex || '#3b82f6' }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Empty space for the other side */}
                <div className="w-5/12" />
              </div>
            );
          })}
        </div>

        {/* Timeline - Mobile */}
        <div className="lg:hidden max-w-lg mx-auto">
          <div className="relative">
            {/* Left line */}
            <div 
              className="absolute left-7 top-0 bottom-0 w-0.5"
              style={{ backgroundColor: `${theme?.primaryHex || '#3b82f6'}30` }}
            />
            
            {journeySteps.map((step, index) => {
              const Icon = step.icon;
              
              return (
                <div key={step.step} className="relative flex mb-8">
                  {/* Icon */}
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center shadow-md z-10 flex-shrink-0"
                    style={{ backgroundColor: theme?.primaryHex || '#3b82f6' }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className="ml-6 flex-1">
                    <Card className="border-0 shadow-md">
                      <CardHeader className="pb-2">
                        <span 
                          className="text-xs font-semibold px-2 py-0.5 rounded-full w-fit"
                          style={{ 
                            backgroundColor: `${theme?.primaryHex || '#3b82f6'}15`,
                            color: theme?.primaryHex || '#3b82f6'
                          }}
                        >
                          {step.duration}
                        </span>
                        <CardTitle className="text-lg mt-2">{step.title}</CardTitle>
                        <p className="text-sm text-gray-500">{step.subtitle}</p>
                      </CardHeader>
                      <CardContent>
                    <p className="text-gray-600 text-sm mb-3">{step.description}</p>
                        <ul className="space-y-1.5">
                          {step.highlights.map((highlight, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                              <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                              {highlight}
                            </li>
                          ))}
                        </ul>
                        {step.actionLink && (
                          <a 
                            href={step.actionLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-3 text-sm font-medium"
                            style={{ color: theme?.primaryHex || '#3b82f6' }}
                          >
                            {step.action}
                            <ArrowRight className="w-4 h-4" />
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">Ready to start your journey to recovery?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={doctor?.consultationLink} target="_blank" rel="noopener noreferrer">
              <button 
                className="px-8 py-3 rounded-lg font-medium text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: theme?.primaryHex || '#3b82f6' }}
              >
                <Calendar className="w-5 h-5 inline-block mr-2" />
                Book Your Consultation
              </button>
            </a>
            <a href={`tel:${doctor?.phone}`}>
              <button className="px-8 py-3 rounded-lg font-medium border-2 border-gray-300 text-gray-700 hover:border-gray-400 transition-colors">
                <Phone className="w-5 h-5 inline-block mr-2" />
                Call {doctor?.phone}
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PatientJourneyTimeline;
